import { useGetNftDropsForUserQuery } from '../../../modules/web3/api'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { Box } from '../../../shared_components/UI/Box'
import { VaultPurchasedContainer } from './styles'
import styled, { css } from 'styled-components'
import { useSelector } from 'react-redux'
import { selectAccount } from '../../../modules/web3/selectors'
import { useEffect, useMemo } from 'react'
import { P, Span, Strong } from '../../../shared_components/Library/Typeography'

export const NftDropsContainer = styled(VaultPurchasedContainer)`
  padding-top: 80px;
  background: transparent;
`

export interface NftDrop {
  id: number
  name: string
  description: string
  image: string
  dateOfDrop: Date
  dropTxHash: string
  gelatoTaskUrl: string
  selectWinnerTxUrl: string
  winnerAddress: string
  hasBeenDropped: boolean
  nftWeightingOfUserAtTimeOfDrop: number
  isWinner?: boolean
  weightAtTimeOfDrop?: string
}

const NftDrops = ({ currentNftWeight = 1 }: { currentNftWeight?: number }) => {
  const account = useSelector(selectAccount)

  const {
    data: nftDropData,
    isLoading,
    refetch,
  } = useGetNftDropsForUserQuery({})

  const allNftDrops = useMemo(() => nftDropData?.data ?? [], [nftDropData])

  useEffect(() => {
    if (!account || !allNftDrops?.length) return
    refetch()
  }, [account, allNftDrops, refetch])

  return (
    <NftDropsContainer g={32} dc fu aw>
      <h4>NFT Drops</h4>
      {isLoading && (
        <Box c>
          <AnimatedLoader />
        </Box>
      )}
      <Box fw g={16} jse>
        {allNftDrops.map((drop: NftDrop) => (
          <NftDropCard
            drop={drop}
            key={drop.id}
            currentNftWeight={currentNftWeight}
          />
        ))}
      </Box>
    </NftDropsContainer>
  )
}

export const Card = styled(Box)`
  border-radius: var(--units-12, 12px);
  background: linear-gradient(0deg, var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 0%, var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04)) 100%), var(--surface-primary, #080808);
  max-width: 100%;
  width: 320px;
  overflow: hidden;

  p {
    height: 60px;
  }
  img {
    width: 288px;
    height: 288px;
    border-radius: var(--units-4, 4px);
  }
  .info-section {
    height: 288px;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    img {
      width: 100%;
      height: auto;
    }
    .info-section {
      height: 400px;
    }
  }
`

export const Section = styled(Box) <{ isGuest: boolean }>`
  border-top: var(--units-1, 1px) solid var(--stroke-tertiary, #252525);
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
  span {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  span.red {
    color: #ff5740;
  }
  img.verified-by-gelato {
    width: 48px;
    height: auto;
  }
`

const RainbowText = styled.span`
  background: var(
    --Rainbow,
    linear-gradient(90deg, #fdfd0f 0%, #fa00ff 53.09%, #0ff 103.33%)
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

export function NftDropCard({
  drop,
  currentNftWeight = 1,
  isGuest,
}: {
  drop: NftDrop
  currentNftWeight?: number
  isGuest?: boolean
}) {
  const isDateInFuture = new Date(drop.dateOfDrop) > new Date()

  const {
    name,
    image,
    description,
    dateOfDrop,
    dropTxHash,
    gelatoTaskUrl,
    hasBeenDropped,
    isWinner,
    weightAtTimeOfDrop,
  } = drop

  const { days, hours, minutes } = getDaysHoursAndMinutesUntilDate(
    new Date(dateOfDrop),
  )

  const dateUntilString = isDateInFuture
    ? `${!!days ? `${days}D` : ''} ${!!hours ? hours + 'H' : ''} ${!!minutes ? minutes + 'M' : ''
    }`
    : 'AWAITING RESULTS'

  return (
    <Card dc fu g={12} pad="m">
      <img src={image} alt={name} />
      <Box dc g={16} className="info-section">
        <Box fu dc g={4}>
          <Strong s="L" fw="semibold">{name}</Strong>
          <P s="M" c="text-secondary">{description}</P>
        </Box>
        <Box dc flex={1}>
          <Section isGuest={isGuest}>
            <span>NFT Drop Weighting</span>
            <span className="yellow">
              {!weightAtTimeOfDrop ? currentNftWeight : weightAtTimeOfDrop}
            </span>
          </Section>
          <Section isGuest={isGuest}>
            <span>Status</span>
            <span>
              {isWinner && hasBeenDropped ? (
                <a href={dropTxHash} target="_blank" rel="noreferrer noopenner">
                  <RainbowText>YOU WON!</RainbowText>
                </a>
              ) : hasBeenDropped ? (
                <a href={dropTxHash} target="_blank" rel="noreferrer noopenner">
                  <span className="yellow">
                    {isGuest ? 'DROPPED' : "YOU DIDN'T WIN"}
                  </span>
                </a>
              ) : isDateInFuture ? (
                <span>
                  Drops in <span className="red">{dateUntilString}</span>
                </span>
              ) : (
                <span className="green">AWAITNG RESULTS</span>
              )}
            </span>
          </Section>
          <Section isGuest={isGuest}>
            <a
              href={
                !!gelatoTaskUrl
                  ? gelatoTaskUrl
                  : 'https://docs.gelato.network/web3-services/vrf/how-does-gelato-vrf-work'
              }
              target="_blank"
              rel="noreferrer noopenner"
            >
              <Span s="M" c="text-brandPrimary" fw="regular">Verified on-chain randomness</Span>
            </a>
            <a
              href="https://docs.gelato.network/web3-services/vrf/how-does-gelato-vrf-work"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src="/img/verified-by-gelato.svg"
                className="verified-by-gelato"
                alt="verified by gelato"
              />
            </a>
          </Section>
        </Box>
      </Box>
    </Card>
  )
}

export default NftDrops

function getDaysHoursAndMinutesUntilDate(date: Date) {
  let days = 0
  let hours = 0
  let minutes = 0
  const isDateInFuture = date > new Date()
  if (isDateInFuture) {
    days = Math.floor(
      (date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
    )
    hours = Math.floor(
      ((date.getTime() - new Date().getTime()) % (1000 * 60 * 60 * 24)) /
      (1000 * 60 * 60),
    )
    minutes = Math.floor(
      ((date.getTime() - new Date().getTime()) % (1000 * 60 * 60)) /
      (1000 * 60),
    )
  }
  return { days, hours, minutes }
}
